import React from "react";
import classNames from "classnames";
import {Spinner} from "@/shared/ui/Spinner";
import styles from "./Button.module.scss";

type Fill =
    | "text"
    | "solid"
    | "outline";

type Size =
    | "s"
    | "m"
    | "l"
    | "xl"
    | "xxl";

type Variant =
    | "primary"
    | "secondary"
    | "tertiary"
    | "icon";

export type Props = React.ButtonHTMLAttributes<HTMLButtonElement> & {
    fill?: Fill;
    size?: Size;
    variant?: Variant;
    loading?: boolean;
    fullWidth?: boolean;
    labelClassName?: string;
    active?: boolean;
};

export const Button = (
    {
        fill = "solid",
        size = "xxl",
        type = "button",
        variant = "primary",
        loading = false,
        className,
        children,
        disabled,
        fullWidth,
        labelClassName,
        active,
        ...otherProps
    }: Props
) => {
    const isDisabled = disabled || loading;

    const buttonClasses = classNames(
        styles.button,
        styles[fill],
        styles[size],
        styles[variant],
        {
            [styles.loading]: loading,
            [styles.fullWidth]: fullWidth && variant !== "icon",
            [styles.active]: active && !isDisabled,
        },
        className,
    );

    const labelClasses = classNames(styles.label, labelClassName);

    return (
        <button type={type} disabled={isDisabled} className={buttonClasses} {...otherProps}>
            <span className={labelClasses}>{children}</span>
            <Spinner show={loading} />
        </button>
    );
};
